import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, FileText, Users, CheckCircle, XCircle } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

const OfficeAnalytics = ({ userData }) => {
    const [usageData, setUsageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getCurrentMonth = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        return `${year}-${month}`;
    };

    useEffect(() => {
        const fetchUsage = async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) {
                setError("Authentication required.");
                setLoading(false);
                return;
            }

            const currentMonth = getCurrentMonth();

            try {
                const response = await api.get(`/offices/usage?month=${currentMonth}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data.success && response.data.data) {
                    setUsageData(response.data.data);
                } else {
                    setError("No usage data available.");
                }
            } catch (err) {
                const msg = err.response?.data?.message || "Failed to load usage data.";
                setError(msg);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: msg,
                    confirmButtonColor: "#1e40af",
                });
            } finally {
                setLoading(false);
            }
        };

        if (userData.isLawFirm) {
            fetchUsage();
        } else {
            setLoading(false);
        }
    }, [userData.isLawFirm]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <XCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <p className="text-red-700 font-medium">{error}</p>
            </div>
        );
    }

    const { usage, limits, can_use_form, current_lawyers } = usageData;

    const currentMonthFormatted = new Date().toLocaleString("en-US", {
        month: "long",
        year: "numeric",
    });

    return (
        <section className="space-y-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Office Analytics</h2>
                    <p className="text-gray-600 mt-1">
                        Usage statistics for <span className="font-medium">{currentMonthFormatted}</span>
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <FileText className="w-5 h-5 text-blue-600" />
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-gray-500">Used / Limit</p>
                            <p className="text-lg font-bold text-gray-900">
                                {usage.form_submissions} / {limits.max_forms}
                            </p>
                        </div>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900">Form Submissions</h3>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                            style={{
                                width: `${(usage.form_submissions / limits.max_forms) * 100}%`,
                            }}
                        />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        {limits.max_forms - usage.form_submissions} remaining
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-gray-500">This Month</p>
                            <p className="text-lg font-bold text-gray-900">{usage.pdf_generations}</p>
                        </div>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900">PDF Generations</h3>
                    <p className="text-xs text-gray-500 mt-1">Tracked per submission</p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <Users className="w-5 h-5 text-green-600" />
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-gray-500">Used / Limit</p>
                            <p className="text-lg font-bold text-gray-900">
                                {current_lawyers} / {limits.max_lawyers}
                            </p>
                        </div>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900">Active Lawyers</h3>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-green-600 h-2 rounded-full transition-all duration-500"
                            style={{
                                width: `${(current_lawyers / limits.max_lawyers) * 100}%`,
                            }}
                        />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        {limits.max_lawyers - current_lawyers} slots available
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex-shrink-0">
                            <div
                                className={`w-10 h-10 rounded-lg flex items-center justify-center ${can_use_form ? "bg-emerald-100" : "bg-red-100"
                                    }`}
                            >
                                {can_use_form ? (
                                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                                ) : (
                                    <XCircle className="w-5 h-5 text-red-600" />
                                )}
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-gray-500">Status</p>
                            <p
                                className={`text-lg font-bold ${can_use_form ? "text-emerald-600" : "text-red-600"
                                    }`}
                            >
                                {can_use_form ? "Active" : "Limited"}
                            </p>
                        </div>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900">Form Access</h3>
                    <p className="text-xs text-gray-500 mt-1">
                        {can_use_form
                            ? "You can submit new forms"
                            : "Monthly limit reached"}
                    </p>
                </motion.div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Monthly Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                        <p className="text-gray-600">Plan Limit</p>
                        <p className="font-medium text-gray-900">
                            {limits.max_forms} forms per month
                        </p>
                    </div>
                    <div>
                        <p className="text-gray-600">Remaining This Month</p>
                        <p className="font-medium text-gray-900">
                            {limits.max_forms - usage.form_submissions} forms
                        </p>
                    </div>
                    <div>
                        <p className="text-gray-600">Next Reset</p>
                        <p className="font-medium text-gray-900">
                            {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)
                                .toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OfficeAnalytics;